import React, { useState } from 'react';
import './module.FreeEstimationForm.css';
import { suryaGroupApi } from './api'
import { useAlert } from '../../../context/AlertContext';


const FreeEstimationForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    subject: '',
    description: '',
    attachment: null
  });

  const { showAlert } = useAlert();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setErrors(prev => ({ ...prev, attachment: 'File size must be less than 10MB' }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        attachment: file
      }));
      setErrors(prev => ({ ...prev, attachment: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Please provide more details (minimum 20 characters)';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);

    try {  
      formData.ip_address = await getIPAddress()
      formData.user_agent = navigator.userAgent 
      try {
         const response = await suryaGroupApi.FreeEstimationReq(formData)
        if (response.data.status) {
          showAlert({ type: 'success', message: response.data.message });
          setSubmitSuccess(true);
          setFormData({
            full_name: '',
            email: '',
            phone: '',
            company_name: '',
            subject: '',
            description: '',
            attachment: null
          });
          setTimeout(() => {
            setSubmitSuccess(false);
            if (onClose) onClose();
          }, 3000);
        }


      } catch (error) {
        if (error.response) {
          // Backend responded with non-2xx status
          const { status, data } = error.response;

          showAlert({
            type: 'error',
            message: data?.message || `Request failed with status ${status}`
          });

        } else if (error.request) {
          // Request made but no response (server down / CORS)
          showAlert({
            type: 'error',
            message: 'Server not responding'
          });

        } else {
          // Something else
          showAlert({
            type: 'error',
            message: error.message
          });
        }
       }



    } catch (error) {
      setErrors({ submit: 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIPAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
       const data = await response.json();
       return data.ip;
    } catch {
      return 'Unknown';
    }
  };

  const removeAttachment = () => {
    setFormData(prev => ({ ...prev, attachment: null }));
  };

  if (submitSuccess) {
    return (
      <div className="estimation-modal">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h3>Request Submitted Successfully!</h3>
          <p>We'll review your request and get back to you within 24 hours.</p>
          <button
            onClick={onClose}
            className="success-btn"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="estimation-modal">
      <div className="estimation-form-container">
        <div className="form-header">
          <h2>Get Your Free Estimation</h2>
          <p className="subtitle">Fill out the form below and we'll get back to you soon</p>
          {onClose && (
            <button className="close-btn" onClick={onClose} aria-label="Close form">
              x
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="estimation-form">
          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          <div className="form-grid-demo">
            <div className="form-group">
              <label htmlFor="full_name">
                Full Name *
                {errors.full_name && <span className="error">{errors.full_name}</span>}
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="John Doe"
                className={errors.full_name ? 'error-input' : ''}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email *
                {errors.email && <span className="error">{errors.email}</span>}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className={errors.email ? 'error-input' : ''}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="form-group">
              <label htmlFor="company_name">Company Name</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your Company Inc."
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject (Optional)</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Brief description of your request"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Project Details *
              {errors.description && <span className="error">{errors.description}</span>}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Please describe your project in detail. Include requirements, timeline, budget range, and any specific needs..."
              rows="6"
              className={errors.description ? 'error-input' : ''}
            />
            <div className="char-count">
              {formData.description.length} characters (minimum 20)
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="attachment">
              Attach File (Optional)
              {errors.attachment && <span className="error">{errors.attachment}</span>}
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                id="attachment"
                name="attachment"
                onChange={handleFileChange}
                className="file-input"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
              />
              <div className="upload-placeholder">
                <svg className="upload-icon" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm4 11H7v-2h10v2zm0-4H7v-2h10v2z" />
                </svg>
                <p>Click to upload or drag and drop</p>
                <p className="file-types">PDF, DOC, JPG, PNG, ZIP (Max 10MB)</p>
              </div>
            </div>

            {formData.attachment && (
              <div className="attachment-preview">
                <span className="file-name">{formData.attachment.name}</span>
                <span className="file-size">
                  ({(formData.attachment.size / 1024 / 1024).toFixed(2)} MB)
                </span>
                <button
                  type="button"
                  onClick={removeAttachment}
                  className="remove-file"
                  aria-label="Remove file"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="form-footer">
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
            <p className="privacy-note">
              By submitting this form, you agree to our privacy policy. We'll never share your information with third parties.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

// Button component to trigger the modal
export const GetFreeEstimationButton = ({ onClick, variant = 'primary' }) => {
  return (
    <button
      onClick={onClick}
      className={`estimation-btn ${variant}`}
    >
      <span className="btn-icon">📋</span>
      Get Free Estimation
    </button>
  );
};

export default FreeEstimationForm;